import { z } from "zod";
import type { FactionId } from "./factions.js";
import { HOME_SECTORS } from "./faction-routes.js";
import { GATES, REGIONS, type SectorId } from "./world-layout.js";

export const STATION_IDS = ["meridian", "harmony", "helion-base", "last-chance", "training-dock"] as const;
export const stationIdSchema = z.enum(STATION_IDS);
export type StationId = (typeof STATION_IDS)[number];
export interface StationDefinition {
  id: StationId;
  name: string;
  factionId: FactionId | null;
  sectorId: SectorId;
  x: number;
  y: number;
  radius: number;
}
function station(id: StationId, name: string, factionId: FactionId | null): StationDefinition {
  const sectorId = factionId === null ? "patrol" : HOME_SECTORS[factionId];
  const region = REGIONS.find((item) => item.id === sectorId);
  if (!region) throw new Error(`Missing station region ${sectorId}`);
  return { id, name, factionId, sectorId, x: region.x + 420, y: region.y + 1100, radius: 150 };
}
export const STATIONS: readonly StationDefinition[] = [
  station("meridian", "Меридиан", null),
  station("harmony", "Гармония", "concord"),
  station("helion-base", "База Гелиона", "helion"),
  station("last-chance", "Последний шанс", "nomads"),
  { id: "training-dock", name: "Учебный док", factionId: null, sectorId: "training", x: 10420, y: 1100, radius: 150 },
];
export function stationDefinition(id: StationId): StationDefinition {
  const result = STATIONS.find((item) => item.id === id);
  if (!result) throw new Error(`Missing station ${id}`);
  return result;
}
export function homeStation(factionId: FactionId | null): StationDefinition {
  if (factionId === null) return stationDefinition("meridian");
  return STATIONS.find((item) => item.factionId === factionId) ?? stationDefinition("meridian");
}
export function canDockAt(station: StationDefinition, factionId: FactionId | null): boolean {
  return station.factionId === null || station.factionId === factionId;
}
export function sectorAt(point: { x: number; y: number }): SectorId | null {
  return REGIONS.find((region) => point.x >= region.x && point.x < region.x + region.width &&
    point.y >= region.y && point.y < region.y + region.height)?.id ?? null;
}
export function nearbyStation(point: { x: number; y: number }): StationDefinition | undefined {
  return STATIONS.find((item) => Math.hypot(item.x - point.x, item.y - point.y) <= item.radius);
}

/** Reachability uses real directed gates; ties use final local distance then stable ID. */
export function nearestStation(point: { x: number; y: number }, factionId: FactionId | null): StationDefinition {
  const origin = sectorAt(point);
  const distances = new Map<SectorId, number>();
  const queue: SectorId[] = [];
  if (origin) { distances.set(origin, 0); queue.push(origin); }
  for (let index = 0; index < queue.length; index++) {
    const current = queue[index];
    if (!current) continue;
    for (const gate of GATES) {
      if (sectorAt(gate) !== current || distances.has(gate.toSectorId)) continue;
      distances.set(gate.toSectorId, (distances.get(current) ?? 0) + 1);
      queue.push(gate.toSectorId);
    }
  }
  const available = STATIONS.filter((item) => item.id !== "training-dock" && canDockAt(item, factionId) && distances.has(item.sectorId));
  available.sort((a, b) => (distances.get(a.sectorId) ?? Infinity) - (distances.get(b.sectorId) ?? Infinity) ||
    Math.hypot(a.x - point.x, a.y - point.y) - Math.hypot(b.x - point.x, b.y - point.y) || a.id.localeCompare(b.id));
  return available[0] ?? homeStation(factionId);
}
